import React from 'react';
import SebiDisclosureFooter from '../components/compliance/SebiDisclosureFooter';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <article className="prose prose-sm dark:prose-invert max-w-3xl mx-auto px-6 py-16">
        <h1>KubAIr Privacy Policy</h1>
        <p className="text-sm text-muted-foreground">
          Last updated: {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
        </p>

        <h2>1. Scope</h2>
        <p>
          This policy describes how KubAIr (&quot;KubAIr&quot;) processes personal data of users of
          KubAIr Research and KubAIr Advisor OS. It is issued in compliance with the Digital Personal
          Data Protection Act, 2023 (&quot;DPDP Act&quot;).
        </p>

        <h2>2. What we collect</h2>
        <table>
          <thead><tr><th>Category</th><th>Examples</th><th>Purpose</th></tr></thead>
          <tbody>
            <tr><td>Identity</td><td>Name, email, hashed password</td><td>Account creation, login</td></tr>
            <tr><td>Retail financial (optional)</td><td>PAN, broker access tokens, holdings</td><td>Portfolio sync, alerts</td></tr>
            <tr><td>Institutional firm data</td><td>Firm name, SEBI registration, address, compliance officer email</td><td>Vendor relationship, branding, audit</td></tr>
            <tr><td>Usage</td><td>Pages viewed, AI queries, pitch reports generated</td><td>Product improvement, billing metering, fraud detection</td></tr>
            <tr><td>Device</td><td>IP address, browser, OS (from logs)</td><td>Security, abuse detection</td></tr>
          </tbody>
        </table>

        <h2>3. How we use it</h2>
        <ul>
          <li>To run and improve the service you signed up for.</li>
          <li>To meter usage against your plan and bill you.</li>
          <li>To send transactional notifications (receipts, alerts you configured).</li>
          <li>To comply with SEBI record-keeping obligations (for Research Analyst output and institutional audit logs).</li>
          <li>To detect and prevent abuse.</li>
        </ul>
        <p>
          We do NOT sell personal data. We do NOT use AI chat conversations to train third-party foundation
          models beyond what the LLM provider (Anthropic) handles for its own tenant-isolated processing.
        </p>

        <h2>4. Sharing with processors</h2>
        <p>We rely on the following processors to run KubAIr:</p>
        <ul>
          <li>MongoDB Atlas, Supabase (Postgres) — primary data stores.</li>
          <li>Anthropic — AI inference.</li>
          <li>OpenAI — document embeddings for pitch report retrieval.</li>
          <li>Resend — transactional email.</li>
          <li>Razorpay / Stripe — payments.</li>
          <li>Sentry — error telemetry.</li>
          <li>PostHog — product analytics (only with your analytics consent).</li>
          <li>Zerodha, Upstox, Angel One — broker OAuth integrations you explicitly connect.</li>
        </ul>

        <h2>5. Encryption at rest</h2>
        <p>
          Sensitive secrets — broker OAuth access tokens, PAN — are encrypted using AES-256-GCM before
          storage. Encryption keys are held separately in our secrets manager and rotated on a schedule.
        </p>

        <h2>6. Your rights under the DPDP Act</h2>
        <p>You can, through <a href="/settings">Settings → Account</a> or by emailing privacy@kubair.in:</p>
        <ul>
          <li>Access: export your complete personal data.</li>
          <li>Correction: update inaccurate data.</li>
          <li>Erasure: delete your account. Regulated records (advisor proposals, audit log) are retained for 5 years per SEBI rules but are disassociated from your identity.</li>
          <li>Consent management: toggle consent for analytics, marketing, broker-data use at any time.</li>
          <li>Grievance: reach our Grievance Officer (grievance@kubair.in). Unresolved complaints may be escalated to the Data Protection Board of India or, for SEBI-linked grievances, to SEBI SCORES.</li>
        </ul>

        <h2>7. Retention</h2>
        <table>
          <thead><tr><th>Data class</th><th>Retention</th></tr></thead>
          <tbody>
            <tr><td>Account identity &amp; preferences</td><td>While account is active; 30 days after deletion request</td></tr>
            <tr><td>Broker OAuth tokens</td><td>Until you disconnect the broker</td></tr>
            <tr><td>SEBI RA research output (pitch reports, analyst notes, audit log)</td><td>5 years (SEBI mandate)</td></tr>
            <tr><td>Payment records &amp; invoices</td><td>8 years (Indian tax law)</td></tr>
            <tr><td>Server logs</td><td>30 days</td></tr>
          </tbody>
        </table>

        <h2>8. Cookies &amp; tracking</h2>
        <p>
          We use essential cookies for authentication. Analytics (PostHog) and marketing cookies are loaded
          only if you opt in. You can change these preferences in Settings → Consent.
        </p>

        <h2>9. Children</h2>
        <p>
          KubAIr is not intended for users under 18. We do not knowingly collect data from children.
        </p>

        <h2>10. Contact</h2>
        <p>
          Grievance Officer: grievance@kubair.in · privacy@kubair.in · KubAIr, Bengaluru, India.
        </p>
      </article>

      <SebiDisclosureFooter />
    </div>
  );
}
